import { Carrier } from '@/data/mobile/carrier';
import { Plan } from '@/data/mobile/plan';
import { Option } from '@/data/mobile/option';
import { SimType } from '@/data/mobile/simType';
import { Terminal } from '@/data/mobile/terminal';
import { CarrierId } from '@/@types/carrierId';
import { SimTypeId } from '@/@types/simTypeId';
import { DeviceTypeId } from '@/@types/deviceTypeId';
import {
  MobileData,
  CarrierData,
  SimData,
  PlanDetail,
  OptionList,
  OptionDetail,
} from '@/@types/json/mobile';
import {
  ApiMobileData,
  ApiCarrierData,
  ApiSimDetail,
  DeviceData,
} from '@/@types/api/mobile';

// tslint:disable-next-line:no-var-requires
const _ = require('lodash');

import commonConfig from '../../config/common.json';
import mobileJson from '../../config/mobile.json';

export class MobileConfig {
  public code: number;
  public universalAndRelayServiceCharge: number;
  public carriers: Carrier[];
  public simTypes: SimType[];
  public plans: Plan[];
  public options: Option[];
  public terminals: Terminal[];

  constructor(
    config: ApiMobileData,
    planAndOptionConfig: MobileData = mobileJson
  ) {
    this.code = planAndOptionConfig.code;
    this.universalAndRelayServiceCharge =
              commonConfig.fee.universal_and_relay_service.charge;
    this.carriers = this.buildCarriers(config);
    this.simTypes = this.buildSimTypes(config);
    this.plans = this.buildPlans(planAndOptionConfig);
    this.options = this.buildOptions(planAndOptionConfig);
    this.terminals = this.buildTerminals(config);
  }

  private buildCarriers(config: ApiMobileData): Carrier[] {
    return _.map(config.carrier, (cData: ApiCarrierData, c: CarrierId) => {
      return new Carrier(
        c,
        cData.name,
        cData.type_name,
        cData.application_fee,
        cData.sim_comission_fee
      );
    });
  }

  private buildSimTypes(config: ApiMobileData): SimType[] {
    return _.map(
      config.carrier.docomo.sim,
      (sData: ApiSimDetail, s: SimTypeId) => {
        return new SimType(s, sData.name);
      }
    );
  }

  private buildPlans(config: MobileData): Plan[] {
    return _.reduce(
      config.lte_3g.carrier,
      (z: Plan[], cData: CarrierData, c: CarrierId) => {
        const carrier = this.findCarrier(c);

        _.each(cData.sim, (sData: SimData, s: SimTypeId) => {
          const simType = new SimType(s, sData.name);
          _.each(sData.plans, (pData: PlanDetail, p: string) => {
            let plan_index = z.findIndex(i => {
              return i.id == p && i.simType.id == s;
            });
            if (plan_index == -1 && carrier) {
              z.push(new Plan(p, simType, carrier, pData));
            } else if (plan_index !== -1 && carrier) {
              const item = z[plan_index];
              item.updateProperties(
                carrier,
                pData['campaign_application_fee'],
                pData['campaign_sim_comission_fee'],
                pData['campaign_sim_plan_fee']
              );
              z.splice(plan_index, 1, item);
            }
          });
        });
        return z;
      },
      []
    );
  }

  private buildOptions(config: MobileData): Option[] {
    return _.reduce(
      config.lte_3g.carrier,
      (z: Option[], cData: CarrierData, c: CarrierId) => {
        const carrier = this.findCarrier(c);
        _.each(cData.options, (gData: OptionList, g: string) => {
          _.each(gData.plans, (oData: OptionDetail, o: string) => {
            let index = z.findIndex(i => {
              return i.id == o;
            });
            if (index == -1) {
              const list = carrier ? [carrier] : [];
              z.push(
                new Option(o, oData.name, oData.charge, g, gData.sim, list)
              );
            } else {
              const old = z[index];
              const item = new Option(
                old.id,
                old.name,
                old.charge,
                old.groupId,
                old.simIds,
                _.concat(old.carriers, carrier)
              );
              z.splice(index, 1, item);
            }
          });
        });
        return z;
      },
      []
    );
  }

  private buildTerminals(config: ApiMobileData): Terminal[] {
    return _.reduce(
      config.carrier,
      (z: Terminal[], cData: ApiCarrierData, c: CarrierId) => {
        _.each(cData.devices, (typeData: DeviceData[], type: DeviceTypeId) => {
          const carrier = this.findCarrier(c);

          typeData.forEach((tData: DeviceData) => {
            const index = z.findIndex(i => {
              return i.id == String(tData.id);
            });
            const valid = tData.device_colors.length > 0;
            if (index == -1 && carrier && valid) {
              // [制限事項] カラバリごとに価格が異なっていた場合無視される
              const price = tData.device_colors[0].price;
              z.push(
                new Terminal(
                  String(tData.id),
                  tData.name,
                  price,
                  tData.image,
                  type,
                  '', // [制限事項] 注意書きなし
                  [carrier]
                )
              );
            } else if (index !== -1 && carrier) {
              const item = z[index];
              item.updateProperty(carrier);
              z.splice(index, 1, item);
            }
          });
        });
        return z;
      },
      []
    );
  }

  // 端末にタイプ順に並べた上で、価格順にソートした端末の一覧を返却する
  // 価格が同じだった場合はjsonファイルの記載順になる
  public sortedTerminals(): Terminal[] {
    const types = ['router', 'sp', 'tablet', 'feature', 'iot'];
    // まずterminalsをfilterしてtypeごとの配列にする
    // その上でsortする
    const listSortedByType = types.map((type: string) => {
      return this.terminals.filter((t: Terminal) => {
        return t.type == type;
      });
    });

    const list = listSortedByType.map((terminals: Terminal[]) => {
      terminals.sort((a: Terminal, b: Terminal) => {
        return a.charge - b.charge;
      });
      return terminals;
    });
    return _.flatten(list);
  }

  public findPlan(planId: string, simId: string): Plan | undefined {
    const p = this.plans.find(plan => {
      const is_equal_id = plan.id === planId;
      const is_equal_sim_type = plan.simType.id === simId;
      return is_equal_id && is_equal_sim_type;
    });
    return p !== undefined ? p : undefined;
  }

  public findOption(id: string): Option | undefined {
    const o = this.options.find(option => {
      return option.id === id;
    });
    return o !== undefined ? o : undefined;
  }

  public findTerminal(id: string): Terminal | undefined {
    const t = this.terminals.find(terminal => {
      return terminal.id === id;
    });
    return t !== undefined ? t : undefined;
  }

  public findCarrier(id: string): Carrier | undefined {
    const c = this.carriers.find(carrier => {
      return carrier.id === id;
    });
    return c !== undefined ? c : undefined;
  }

  public filteredPlans(simType: string): Plan[] {
    return this.plans.filter(p => {
      return p.simType.id == simType;
    });
  }

  public filteredOptions(simType: string): Option[] {
    return this.options.filter(o => {
      return _.includes(o.simIds, simType);
    });
  }

  public applicationFee(carrier: string): number | undefined {
    const c = this.carriers.find(item => {
      return item.id == carrier;
    });
    return c == undefined ? undefined : c.applicationFee;
  }

  public simComissionFee(carrier: string): number | undefined {
    const c = this.carriers.find(item => {
      return item.id == carrier;
    });
    return c == undefined ? undefined : c.simComissionFee;
  }
}
